/**
 * Verification review console — the request queue + approve/reject.
 *
 * Staff (admin / super_admin) review user-submitted verification
 * requests (apps/web /settings/verification) and decide:
 *   - Approve         → worker writes the verifications row, sets the
 *                       user's real_name and LOCKS display_name to it
 *   - Reject          → decision note explains why
 *   - Needs more info → bounced back to the user with a note
 *
 * Queue order mirrors the worker: priority lane first (launch-special /
 * $1 fast-lane requests jump the line), paid first, oldest first.
 * Evidence documents stream through the Access-gated bridge
 * (/api/v2/verification/documents/<key>) — images render inline,
 * PDFs open in a tab.
 *
 * Destructive actions use the two-step "click again to confirm"
 * pattern (no window.confirm — §0c restraint, same as Moderation).
 */

import { livenessDecidedLabel, livenessOrderLabel } from '../lib/liveness-labels';
import { createFileRoute } from '@tanstack/react-router';
import { isIndependentSource, parseServerDate } from '@postopia/shared';
import { useCallback, useEffect, useState } from 'react';
import { TabBar } from '../components/ui/TabBar';
import { AdminShell } from '../components/AdminShell';
import { EvidenceLightbox, type EvidenceItem } from '../components/EvidenceLightbox';
import {
  verificationApi,
  VerificationApiError,
  type AdminCredential,
  type AdminProfession,
  type IdvJobRow,
  type VerificationReversal,
  type SubmissionSibling,
  type VerificationLedgerRow,
  type VerificationRequestDetail,
  type VerificationRequestRow,
} from '../lib/verificationApi';
import { useIdvDocUrls, useIdvDocUrlsFor } from '../lib/idvDocs';

export const Route = createFileRoute('/verification')({
  component: VerificationRoute,
});

function VerificationRoute() {
  return (
    <AdminShell mode="feature" featureName="Verification">
      <VerificationConsole />
    </AdminShell>
  );
}

const TYPE_LABEL: Record<string, string> = {
  identity_verified: 'Verified Person (ID)',
  notable_person: 'Notable Public Figure',
  press_journalist: 'Journalist',
  news_organization: 'News Organization',
  business_org: 'Business / Organization',
  gov_official: 'Government Official',
  gov_entity: 'Government / Public Body',
  credentialed_expert: 'Credentialed Expert',
};

const STATUS_STYLE: Record<string, { bg: string; fg: string }> = {
  pending: { bg: 'var(--c-bg-surface-alt)', fg: 'var(--c-fg-secondary)' },
  in_review: { bg: '#EFE3CF', fg: '#7A5A1A' },
  needs_more_info: { bg: '#F5E4CF', fg: '#8A5A1A' },
  approved: { bg: '#DFE8D5', fg: '#3F5A2A' },
  rejected: { bg: '#F4D9D2', fg: '#9A2A12' },
  withdrawn: { bg: 'var(--c-bg-surface-alt)', fg: 'var(--c-fg-tertiary)' },
};

type PendingDecision = { id: string; action: 'approve' | 'reject' } | null;

function VerificationConsole() {
  const [tab, setTab] = useState<'requests' | 'verifications' | 'idv'>('requests');
  const [queue, setQueue] = useState<VerificationRequestRow[] | null>(null);
  const [scope, setScope] = useState<'open' | 'all'>('open');
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<VerificationRequestDetail | null>(null);
  const [siblings, setSiblings] = useState<SubmissionSibling[]>([]);
  const [reversals, setReversals] = useState<VerificationReversal[]>([]);
  const [note, setNote] = useState('');
  const [pending, setPending] = useState<PendingDecision>(null);
  const [reverseReason, setReverseReason] = useState('');
  const [reverseArmed, setReverseArmed] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await verificationApi.queue(scope);
      setQueue(res.requests);
    } catch (err) {
      if (err instanceof VerificationApiError && err.status === 403) {
        setError('Your account does not have verification-review access.');
      } else if (
        err instanceof VerificationApiError &&
        err.code === 'verification_bridge_not_configured'
      ) {
        setError('Verification review is not configured on this environment yet.');
      } else {
        setError("Couldn't load the queue. Try again.");
      }
      setQueue([]);
    }
  }, [scope]);

  useEffect(() => {
    void load();
  }, [load]);

  const openDetail = useCallback(async (id: string) => {
    setOpenId(id);
    setDetail(null);
    setNote('');
    setPending(null);
    setReverseArmed(false);
    try {
      const res = await verificationApi.detail(id);
      setDetail(res.request);
      setSiblings(res.siblings ?? []);
      setReversals(res.reversals ?? []);
    } catch {
      setStatus("Couldn't load that request.");
      setOpenId(null);
    }
  }, []);

  const decide = useCallback(
    async (action: 'approve' | 'reject' | 'needs_more_info') => {
      if (!openId) return;
      // Two-step confirm for approve/reject; needs_more_info fires once
      // (it's reversible — the request stays open).
      if (action !== 'needs_more_info') {
        if (!pending || pending.id !== openId || pending.action !== action) {
          setPending({ id: openId, action });
          return;
        }
      }
      setPending(null);
      setBusy(true);
      setStatus(null);
      try {
        await verificationApi.decide(openId, action, note.trim() || undefined);
        setStatus(
          action === 'approve'
            ? 'Approved — verification written, real name locked.'
            : action === 'reject'
              ? 'Rejected.'
              : 'Sent back for more info.',
        );
        setOpenId(null);
        setDetail(null);
        await load();
      } catch (err) {
        setStatus(
          err instanceof VerificationApiError && err.code === 'already_decided'
            ? 'Already decided by another reviewer.'
            : 'Decision failed. Try again.',
        );
      } finally {
        setBusy(false);
      }
    },
    [openId, pending, note, load],
  );

  /**
   * Overturn an AI decision (migration 0343). This REOPENS the request rather
   * than deciding it: on success the panel refreshes and the ordinary
   * Approve / Reject / Needs-more-info controls appear, because the reviewer
   * reversed it in order to decide it themselves.
   *
   * The reason is its own field, not the decision note. They answer different
   * questions — "why is the machine wrong" versus "what am I telling the
   * applicant" — and the server stores them in different places.
   */
  const reverse = useCallback(async () => {
    if (!openId) return;
    const reason = reverseReason.trim();
    if (reason.length < 10) {
      setStatus('Give a reason of at least 10 characters — the server requires one.');
      return;
    }
    // Two-step, like approve/reject: reversing an AI APPROVAL revokes a live
    // badge and can cascade to other people's badges.
    if (!reverseArmed) {
      setReverseArmed(true);
      return;
    }
    setReverseArmed(false);
    setBusy(true);
    setStatus(null);
    try {
      const res = await verificationApi.reverse(openId, reason);
      const bits = ['Reversed — the request is back in the queue as In review.'];
      if (res.revoked_verification) {
        bits.push(
          res.cascaded_vouches > 0
            ? `The badge was revoked, and ${res.cascaded_vouches} vouched badge(s) fell with it.`
            : 'The badge it had written was revoked.',
        );
      }
      if (res.evidence_present === false) {
        bits.push('The submitted files are no longer in storage — decide on other evidence.');
      }
      setStatus(bits.join(' '));
      setReverseReason('');
      await load();
      // Straight back into the same request, now decidable.
      await openDetail(openId);
    } catch (err) {
      setStatus(
        err instanceof VerificationApiError
          ? err.message
          : 'Reversal failed. Try again.',
      );
    } finally {
      setBusy(false);
    }
  }, [openId, reverseReason, reverseArmed, load, openDetail]);

  return (
    <main
      style={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        background: 'var(--c-bg-page)',
        padding: '40px 24px',
        overflowY: 'auto',
      }}
    >
      <div style={{ width: '100%', maxWidth: 860 }}>
        <header style={{ marginBottom: 14 }}>
          <h1 style={{ fontFamily: '"Source Serif 4", Georgia, serif', fontWeight: 600, fontSize: 26, letterSpacing: '-0.01em', color: 'var(--c-fg-default)', margin: 0 }}>
            Verification
          </h1>
        </header>

        {/* Requests = the review QUEUE (manual lane). Verifications = the FACTS
            ledger (incl. Lane A automated IDV + the retained ID image).
            ID checks = the Lane A idv_jobs queue itself — the ONLY place a
            REJECTED automated check is visible (a rejection writes no
            verifications row; founder 2026-08-16). */}
        <div style={{ marginBottom: 18 }}>
          <TabBar
            label="Verification views"
            activeKey={tab}
            onSelect={setTab}
            items={[
              { key: 'requests', label: 'Requests' },
              { key: 'verifications', label: 'Verifications' },
              { key: 'idv', label: 'ID checks' },
            ]}
          />
        </div>

        {tab === 'requests' ? (
          <>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 6 }}>
              <button
                type="button"
                onClick={() => setScope((s) => (s === 'open' ? 'all' : 'open'))}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--c-border-hairline)',
                  borderRadius: 9999,
                  padding: '3px 12px',
                  fontFamily: 'Inter',
                  fontSize: 12,
                  color: 'var(--c-fg-secondary)',
                  cursor: 'pointer',
                }}
              >
                {scope === 'open' ? 'Showing open · view all' : 'Showing all · view open'}
              </button>
            </div>
            <p style={{ fontFamily: 'Inter', fontSize: 13, color: 'var(--c-fg-secondary)', margin: '0 0 18px' }}>
              Oldest first. Approving writes the verification and stores the real
              name privately; the public display name stays the user's own.
            </p>

            {status ? (
              <p style={{ fontFamily: 'Inter', fontSize: 13, color: 'var(--c-fg-default)', margin: '0 0 12px' }}>
                {status}
              </p>
            ) : null}
            {error ? (
              <p style={{ fontFamily: 'Inter', fontSize: 14, color: 'var(--c-state-danger, #C7563E)', margin: '24px 0' }}>{error}</p>
            ) : queue === null ? (
              <p style={{ fontFamily: 'Inter', fontSize: 14, color: 'var(--c-fg-secondary)' }}>Loading…</p>
            ) : queue.length === 0 ? (
              <p style={{ fontFamily: 'Inter', fontSize: 14, color: 'var(--c-fg-secondary)', margin: '24px 0' }}>
                No {scope === 'open' ? 'open ' : ''}requests.
              </p>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {queue.map((r) => (
                  <QueueRow key={r.id} row={r} open={openId === r.id} onOpen={() => void openDetail(r.id)} />
                ))}
              </ul>
            )}

            {openId && detail ? (
              <DetailPanel
                detail={detail}
                siblings={siblings}
                note={note}
                setNote={setNote}
                pending={pending}
                busy={busy}
                reversals={reversals}
                reverseReason={reverseReason}
                setReverseReason={setReverseReason}
                reverseArmed={reverseArmed}
                onReverse={reverse}
                onDecide={decide}
                onClose={() => {
                  setOpenId(null);
                  setDetail(null);
                  setPending(null);
                }}
                onReload={() => void openDetail(openId)}
              />
            ) : null}
          </>
        ) : tab === 'verifications' ? (
          <VerificationsLedger />
        ) : (
          <IdvJobsQueue />
        )}
      </div>
    </main>
  );
}

/**
 * IdvJobsQueue — the Lane A automated-IDV queue, every outcome (founder
 * 2026-08-16: submissions "don't seem to reach anywhere"). The ledger tab
 * only shows APPROVED checks (a rejection writes no verifications row), so a
 * rejected or in-flight check was invisible from this console — staff had no
 * way to tell "rejected for liveness" apart from "vanished". Read-only: the
 * automated verdict is final; a rejected user retries in-app after cooldown.
 */
/** Evidence items for one job — the same lightbox + documents bridge the
 *  request queue uses (founder 2026-08-16: "no way to see the documents …
 *  in case decision must be reversed"). Present only while the job's review
 *  window holds; verified jobs show theirs via the ledger's retained docs. */
function idvEvidenceKeys(j: IdvJobRow): Array<{ key: string; label: string }> {
  const img = j.images ?? {};
  const out: Array<{ key: string; label: string }> = [];
  if (img.id) out.push({ key: img.id, label: 'ID — front' });
  if (img.idBack) out.push({ key: img.idBack, label: 'ID — back' });
  if (img.selfie) out.push({ key: img.selfie, label: 'Selfie' });
  for (const [i, p] of (img.poses ?? []).entries()) {
    if (p) out.push({ key: p, label: `Head-turn ${i + 1}` });
  }
  return out;
}

/** `docUrl` resolves a scoped key to its staff-pass blob URL (lib/idvDocs). */
function idvEvidenceItems(j: IdvJobRow, docUrl: (key: string) => string): EvidenceItem[] {
  return idvEvidenceKeys(j).map((d) => ({ url: docUrl(d.key), label: d.label, isImage: true }));
}

function IdvJobsQueue() {
  const [rows, setRows] = useState<IdvJobRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Open lightbox: which job + which item. */
  const [viewer, setViewer] = useState<{ jobId: string; index: number } | null>(null);
  /** Which job's cooldown-clear form is open, and its in-flight state. */
  const [clearing, setClearing] = useState<string | null>(null);
  const [clearReason, setClearReason] = useState('');
  const [clearBusy, setClearBusy] = useState(false);
  const [clearError, setClearError] = useState<string | null>(null);
  /** Every job's evidence keys with its own user: scoped keys are fetched
   *  with that user's staff pass (CHAT_MEDIA_PRIVACY_PLAN §6). */
  const docUrl = useIdvDocUrlsFor(
    (rows ?? []).flatMap((j) => idvEvidenceKeys(j).map((d) => ({ key: d.key, userId: j.user_id }))),
  );

  /** Grant the override, then reflect it in place — no refetch, so the
   *  operator sees the result attached to the row they acted on. */
  async function submitClear(jobId: string) {
    setClearBusy(true);
    setClearError(null);
    try {
      await verificationApi.clearIdvCooldown(jobId, clearReason.trim());
      setRows((prev) =>
        (prev ?? []).map((r) =>
          r.id === jobId
            ? {
                ...r,
                override_by: 'you (just now)',
                override_at: new Date().toISOString(),
                override_reason: clearReason.trim(),
              }
            : r,
        ),
      );
      setClearing(null);
      setClearReason('');
    } catch (err) {
      setClearError(
        err instanceof VerificationApiError && err.status === 400
          ? 'Give a reason of at least 10 characters.'
          : "Couldn't clear the cooldown. Try again.",
      );
    } finally {
      setClearBusy(false);
    }
  }

  const reload = useCallback(async () => {
    try {
      const res = await verificationApi.idvJobs('all');
      setRows(res.jobs);
    } catch (err) {
      setRows([]);
      setError(
        err instanceof VerificationApiError && err.status === 403
          ? 'Your account does not have verification-review access.'
          : "Couldn't load ID checks. Try again.",
      );
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const badge = (s: string) => {
    const style =
      (s === 'verified'
        ? STATUS_STYLE.approved
        : s === 'rejected' || s === 'failed'
          ? STATUS_STYLE.rejected
          : STATUS_STYLE.pending) ?? { bg: 'var(--c-bg-surface-alt)', fg: 'var(--c-fg-secondary)' };
    return (
      <span
        style={{
          background: style.bg,
          color: style.fg,
          borderRadius: 9999,
          padding: '2px 10px',
          fontFamily: 'Inter',
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        {s}
      </span>
    );
  };

  return (
    <div>
      <p style={{ fontFamily: 'Inter', fontSize: 13, color: 'var(--c-fg-secondary)', margin: '0 0 16px' }}>
        Automated government-ID checks (Lane A), newest first — including
        rejections, which never reach the Verifications ledger. The reason
        shown is exactly what the user saw; verified checks appear on the
        Verifications tab too, with the retained ID image.
      </p>
      <p style={{ fontFamily: 'Inter', fontSize: 13, color: 'var(--c-fg-secondary)', margin: '0 0 16px' }}>
        A reviewer’s action never rewrites the machine’s verdict, so a check you
        approve by hand keeps its <em>rejected</em> status and gains an{' '}
        <strong>approved by staff</strong> chip beside it. The badge is live either
        way — the Verifications tab is where it shows.
      </p>
      {error ? (
        <p style={{ fontFamily: 'Inter', fontSize: 14, color: 'var(--c-state-danger, #C7563E)', margin: '24px 0' }}>{error}</p>
      ) : rows === null ? (
        <p style={{ fontFamily: 'Inter', fontSize: 14, color: 'var(--c-fg-secondary)' }}>Loading…</p>
      ) : rows.length === 0 ? (
        <p style={{ fontFamily: 'Inter', fontSize: 14, color: 'var(--c-fg-secondary)' }}>
          No ID checks yet on this environment.
        </p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10 }}>
          {rows.map((j) => (
            <li
              key={j.id}
              style={{
                border: '1px solid var(--c-border-hairline)',
                borderRadius: 8,
                padding: '12px 14px',
                background: 'var(--c-bg-surface)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                {badge(j.status)}
                {/* The machine's verdict above is never rewritten — that is
                    the whole rule (0344). But a row that still reads
                    "rejected" after a reviewer has granted the badge reads as
                    "nothing happened", which is exactly how it was read the
                    first time one was approved. So the human outcome gets its
                    own chip beside the machine's, rather than living only in
                    the small print at the bottom of the card. */}
                {(j.staff_decisions ?? []).some((d) => d.action === 'approve') ? (
                  <span
                    style={{
                      background: '#E7EDE1',
                      color: '#3F5A2A',
                      borderRadius: 9999,
                      padding: '2px 10px',
                      fontFamily: 'Inter',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                    title="A reviewer granted the badge. The automated verdict is kept as it was recorded."
                  >
                    approved by staff
                  </span>
                ) : (j.staff_decisions ?? []).some((d) => d.action === 'rerun') ? (
                  <span
                    style={{
                      background: 'var(--c-bg-surface-alt)',
                      color: 'var(--c-fg-secondary)',
                      borderRadius: 9999,
                      padding: '2px 10px',
                      fontFamily: 'Inter',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                    title="A reviewer sent this evidence through the checker again; the new check carries the outcome."
                  >
                    re-judged
                  </span>
                ) : null}
                <span style={{ fontFamily: 'Inter', fontSize: 14, fontWeight: 600, color: 'var(--c-fg-default)' }}>
                  {j.user_display_name ?? '(deleted user)'}
                </span>
                <span style={{ fontFamily: 'Inter', fontSize: 13, color: 'var(--c-fg-secondary)' }}>
                  {j.user_handle ? `@${j.user_handle}` : ''}
                </span>
                <span style={{ marginLeft: 'auto', fontFamily: 'Inter', fontSize: 12, color: 'var(--c-fg-tertiary)' }}>
                  {parseServerDate(j.created_at).toLocaleString()}
                  {j.decision_seconds != null ? ` · decided in ${j.decision_seconds}s` : ' · undecided'}
                </span>
              </div>
              {j.verdict ? (
                <div style={{ marginTop: 6, fontFamily: 'Inter', fontSize: 13, color: 'var(--c-fg-secondary)' }}>
                  {j.verdict.reason ? <div style={{ color: 'var(--c-fg-default)' }}>{j.verdict.reason}</div> : null}
                  <div style={{ marginTop: 2, color: 'var(--c-fg-tertiary)', fontSize: 12 }}>
                    {[
                      j.verdict.reason_code ? `code: ${j.verdict.reason_code}` : null,
                      j.verdict.doc_type ? `doc: ${j.verdict.doc_type}` : null,
                      j.verdict.id_country ? `country: ${j.verdict.id_country}` : null,
                      j.verdict.name_match ? `name: ${j.verdict.name_match}` : null,
                      typeof j.verdict.face_match_score === 'number'
                        ? `face: ${j.verdict.face_match_score.toFixed(2)}`
                        : null,
                      j.verdict.liveness_passed === false ? 'liveness: failed' : null,
                      j.verdict.paper_print ? 'paper print' : null,
                      j.verdict.mrz_state && j.verdict.mrz_state !== 'valid' && j.verdict.mrz_state !== 'absent'
                        ? `mrz: ${j.verdict.mrz_state}`
                        : null,
                      j.verdict.front_consistent === false ? 'front/machine-data mismatch' : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                  {j.verdict.detect?.available ? (
                    // Synthetic-image detect (flag mode — recorded, not gating).
                    // Same score scale as media_ai_detection; a flag = over threshold.
                    <div style={{ marginTop: 2, color: j.verdict.detect.id_flag || j.verdict.detect.selfie_flag ? 'var(--c-fg-default)' : 'var(--c-fg-tertiary)', fontSize: 12 }}>
                      {[
                        `synthetic-detect (${j.verdict.detect.mode}, thr ${j.verdict.detect.threshold})`,
                        `id: ${j.verdict.detect.id_score}${j.verdict.detect.id_flag ? ' FLAG' : ''}`,
                        Array.isArray(j.verdict.detect.selfie_scores) && j.verdict.detect.selfie_scores.length
                          ? `selfie set: [${j.verdict.detect.selfie_scores.join(', ')}]${j.verdict.detect.selfie_flag ? ' FLAG' : ''}`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  ) : null}
                  {j.verdict.face_method ? (
                    <div style={{ marginTop: 2, color: 'var(--c-fg-tertiary)', fontSize: 12 }}>
                      {[
                        `face: ${typeof j.verdict.face_match_score === 'number' ? j.verdict.face_match_score.toFixed(4) : '—'} via ${j.verdict.face_method}`,
                        j.verdict.face_all_similarities?.length
                          ? `all: [${j.verdict.face_all_similarities.map((n) => n.toFixed(4)).join(', ')}]`
                          : null,
                        j.verdict.face_discarded_artifacts
                          ? `${j.verdict.face_discarded_artifacts} artwork face(s) excluded`
                          : null,
                        j.verdict.face_box_missed ? 'photo-box held no face' : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  ) : null}
                  {j.verdict.liveness_detail ? (
                    <div style={{ marginTop: 2, color: 'var(--c-fg-tertiary)', fontSize: 12 }}>
                      {[
                        j.verdict.liveness_detail.challenge?.length
                          ? `asked: ${j.verdict.liveness_detail.challenge.join('→')}`
                          : null,
                        j.verdict.liveness_detail.frames?.length
                          ? `seen: ${j.verdict.liveness_detail.frames
                              .map((f) => `${f.headYaw ?? '?'}${f.faceDetected ? '' : '(no face)'}${f.looksLive ? '' : '(not live)'}`)
                              .join('→')}`
                          : 'seen: no frames',
                        j.verdict.liveness_detail.photo_of_photo ? 'photo-of-photo' : null,
                        livenessOrderLabel(j.verdict.liveness_detail.sequence_match),
                        livenessDecidedLabel(j.verdict.liveness_detail),
                        j.verdict.liveness_detail.gemini_reason || null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  ) : null}
                </div>
              ) : null}
              {(() => {
                const items = idvEvidenceItems(j, docUrl);
                if (items.length === 0) return null;
                return (
                  <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {items.map((it, i) => (
                      <button
                        key={it.label}
                        type="button"
                        onClick={() => setViewer({ jobId: j.id, index: i })}
                        style={{
                          background: 'var(--c-bg-surface-alt)',
                          border: '1px solid var(--c-border-hairline)',
                          borderRadius: 6,
                          padding: '4px 10px',
                          fontFamily: 'Inter',
                          fontSize: 12,
                          color: 'var(--c-fg-default)',
                          cursor: 'pointer',
                        }}
                      >
                        {it.label}
                      </button>
                    ))}
                  </div>
                );
              })()}
              {(() => {
                // Only offer this where it would DO something: the submission
                // gate reads the user's newest job only, so clearing an older
                // one is a no-op — and a button that silently does nothing is
                // worse than no button. In-flight jobs have no cooldown yet.
                const decided = j.status !== 'pending' && j.status !== 'processing';
                const actionable = decided && j.is_latest && j.within_cooldown;
                if (j.override_by) {
                  return (
                    <div
                      style={{
                        marginTop: 8,
                        fontFamily: 'Inter',
                        fontSize: 12,
                        color: 'var(--c-fg-tertiary)',
                        borderTop: '1px solid var(--c-border-hairline)',
                        paddingTop: 8,
                      }}
                    >
                      Cooldown cleared by {j.override_by}
                      {j.override_at ? ` on ${parseServerDate(j.override_at).toLocaleString()}` : ''}
                      {j.override_reason ? ` — ${j.override_reason}` : ''}
                    </div>
                  );
                }
                if (!actionable) return null;
                if (clearing !== j.id) {
                  return (
                    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      {j.cooldown_until ? (
                        <span style={{ fontFamily: 'Inter', fontSize: 12, color: 'var(--c-fg-tertiary)' }}>
                          Cooldown until {parseServerDate(j.cooldown_until).toLocaleString()}
                        </span>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => {
                          setClearing(j.id);
                          setClearReason('');
                          setClearError(null);
                        }}
                        style={{
                          background: 'transparent',
                          border: '1px solid var(--c-border-strong)',
                          borderRadius: 6,
                          padding: '5px 12px',
                          fontFamily: 'Inter',
                          fontSize: 12,
                          fontWeight: 600,
                          color: 'var(--c-fg-default)',
                          cursor: 'pointer',
                        }}
                      >
                        Clear cooldown
                      </button>
                    </div>
                  );
                }
                return (
                  <div
                    style={{
                      marginTop: 8,
                      borderTop: '1px solid var(--c-border-hairline)',
                      paddingTop: 8,
                      display: 'grid',
                      gap: 6,
                    }}
                  >
                    <label
                      htmlFor={`clear-${j.id}`}
                      style={{ fontFamily: 'Inter', fontSize: 12, color: 'var(--c-fg-secondary)' }}
                    >
                      Why is this cooldown being cleared? Recorded against your
                      account, permanently. At least 10 characters.
                    </label>
                    <textarea
                      id={`clear-${j.id}`}
                      value={clearReason}
                      onChange={(e) => setClearReason(e.target.value)}
                      aria-describedby={`clear-${j.id}-min`}
                      rows={2}
                      style={{
                        fontFamily: 'Inter',
                        fontSize: 13,
                        padding: '6px 8px',
                        borderRadius: 6,
                        border: '1px solid var(--c-border-hairline)',
                        background: 'var(--c-bg-surface-alt)',
                        color: 'var(--c-fg-default)',
                        resize: 'vertical',
                      }}
                    />
                    {clearReason.trim().length < 10 ? (
                      <p
                        id={`clear-${j.id}-min`}
                        style={{ fontFamily: 'Inter', fontSize: 12, color: 'var(--c-fg-tertiary)', margin: 0 }}
                      >
                        {clearReason.trim().length}/10 characters — Confirm unlocks at 10.
                      </p>
                    ) : null}
                    {clearError ? (
                      <p role="alert" style={{ fontFamily: 'Inter', fontSize: 12, color: 'var(--c-state-danger, #C7563E)', margin: 0 }}>
                        {clearError}
                      </p>
                    ) : null}
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        type="button"
                        disabled={clearBusy || clearReason.trim().length < 10}
                        title={
                          clearReason.trim().length < 10
                            ? 'Enter a reason of at least 10 characters first'
                            : undefined
                        }
                        onClick={() => void submitClear(j.id)}
                        style={{
                          background: 'var(--c-fg-default)',
                          color: 'var(--c-bg-page)',
                          border: 'none',
                          borderRadius: 6,
                          padding: '6px 14px',
                          fontFamily: 'Inter',
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: clearBusy || clearReason.trim().length < 10 ? 'not-allowed' : 'pointer',
                          opacity: clearBusy || clearReason.trim().length < 10 ? 0.5 : 1,
                        }}
                      >
                        {clearBusy ? 'Clearing…' : 'Confirm'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setClearing(null)}
                        style={{
                          background: 'transparent',
                          border: '1px solid var(--c-border-hairline)',
                          borderRadius: 6,
                          padding: '6px 14px',
                          fontFamily: 'Inter',
                          fontSize: 12,
                          color: 'var(--c-fg-secondary)',
                          cursor: 'pointer',
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                );
              })()}
              <StaffIdvActions job={j} onDone={reload} />
            </li>
          ))}
        </ul>
      )}
      {viewer
        ? (() => {
            const j = rows?.find((r) => r.id === viewer.jobId);
            const items = j ? idvEvidenceItems(j, docUrl) : [];
            if (items.length === 0) return null;
            return (
              <EvidenceLightbox
                items={items}
                index={Math.min(viewer.index, items.length - 1)}
                onClose={() => setViewer(null)}
                onIndex={(next) => setViewer({ jobId: viewer.jobId, index: next })}
              />
            );
          })()
        : null}
    </div>
  );
}

/**
 * StaffIdvActions — the two things an admin may do about a REJECTED identity
 * check (founder 2026-09-21: "Allow the admin to resend it through again or
 * manually approve it").
 *
 * They are deliberately not equal. Running it again is the first move and is
 * styled as the plain one; approving by hand is the fallback and carries the
 * warning, because only the rerun can register the ID's fingerprint.
 *
 * Neither rewrites the machine's rejection. That stays exactly as recorded,
 * and each human act is a row beside it, the same rule Clear cooldown above
 * already follows.
 */
function StaffIdvActions({ job, onDone }: { job: IdvJobRow; onDone: () => Promise<void> | void }) {
  const [form, setForm] = useState<'rerun' | 'approve' | null>(null);
  const [reason, setReason] = useState('');
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const history = job.staff_decisions ?? [];
  const rejected = job.status === 'rejected' || job.status === 'failed';
  // The name the account already carries. This is what the check compared the
  // document against, so verdict.name_match is its verdict on precisely this.
  const onFileFirst = job.user_first_name?.trim() ?? '';
  const onFileLast = job.user_last_name?.trim() ?? '';
  const onFile = job.user_real_name?.trim() || [onFileFirst, onFileLast].filter(Boolean).join(' ');
  const nameVerdict = job.verdict?.name_match ?? null;
  // The worker refuses a manual approval on a non-adult band because the minor
  // protections (force private, re-scope public posts, sweep chat requests)
  // only run on the box, down the rerun path. Mirrored here so the button
  // explains itself rather than 409-ing after a click.
  const ageBand = job.verdict?.age_band ?? null;
  const minorBand = Boolean(ageBand) && ageBand !== 'adult';
  // The ID is already registered to another verified account. The worker
  // refuses a manual approval outright; the button says so rather than
  // letting the click find out.
  const duplicate = job.duplicate === true;
  const approveBlocked = minorBand || duplicate;

  async function submit(kind: 'rerun' | 'approve') {
    setBusy(true);
    setErr(null);
    try {
      if (kind === 'rerun') {
        const res = await verificationApi.rerunIdvJob(job.id, reason.trim());
        setDone(`Queued as check ${res.job_id}. The box will judge it within a minute.`);
      } else {
        const res = await verificationApi.approveIdvJob(job.id, {
          reason: reason.trim(),
          ...(first.trim() ? { firstName: first.trim() } : {}),
          ...(last.trim() ? { lastName: last.trim() } : {}),
        });
        setDone(`Approved as ${res.real_name}. No ID fingerprint was registered.`);
      }
      setForm(null);
      setReason('');
      await onDone();
    } catch (e) {
      setErr(
        e instanceof VerificationApiError
          ? e.message
          : 'That did not go through. Try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  if (!rejected && history.length === 0) return null;

  return (
    <div
      style={{
        marginTop: 8,
        borderTop: '1px solid var(--c-border-hairline)',
        paddingTop: 8,
        display: 'grid',
        gap: 8,
      }}
    >
      {history.map((h) => (
        <div key={h.id} style={{ fontFamily: 'Inter', fontSize: 12, color: 'var(--c-fg-secondary)' }}>
          {h.action === 'rerun' ? 'Re-judged' : 'Approved by hand'} by {h.decided_by} on{' '}
          {parseServerDate(h.created_at).toLocaleString()} — “{h.reason}”
          {h.action === 'rerun' && h.rerun_job_id ? ` (new check ${h.rerun_job_id})` : ''}
          {h.action === 'approve' && !h.fingerprint_registered ? (
            <span style={{ color: 'var(--c-state-danger, #C7563E)' }}> · no ID fingerprint registered</span>
          ) : null}
        </div>
      ))}

      {done ? (
        <p style={{ fontFamily: 'Inter', fontSize: 12, color: 'var(--c-fg-default)', margin: 0 }}>{done}</p>
      ) : null}

      {!rejected ? null : job.already_verified ? (
        <p style={{ fontFamily: 'Inter', fontSize: 12, color: 'var(--c-fg-tertiary)', margin: 0 }}>
          This person is already verified, so there is nothing to act on.
        </p>
      ) : job.open_job ? (
        <p style={{ fontFamily: 'Inter', fontSize: 12, color: 'var(--c-fg-tertiary)', margin: 0 }}>
          A check for this person is in flight. Wait for it to finish.
        </p>
      ) : form === null ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => {
              setForm('rerun');
              setReason('');
              setErr(null);
            }}
            style={btn.plain}
          >
            Run it again
          </button>
          <button
            type="button"
            onClick={() => {
              setForm('approve');
              setReason('');
              setFirst(onFileFirst);
              setLast(onFileLast);
              setErr(null);
            }}
            disabled={approveBlocked}
            title={
              duplicate
                ? 'This ID is already registered to another verified account'
                : minorBand
                  ? 'Non-adult age band — use Run it again'
                  : undefined
            }
            style={{ ...btn.danger, opacity: approveBlocked ? 0.45 : 1, cursor: approveBlocked ? 'not-allowed' : 'pointer' }}
          >
            Approve by hand
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 6 }}>
          <p style={{ fontFamily: 'Inter', fontSize: 12, color: 'var(--c-fg-secondary)', margin: 0, lineHeight: 1.5 }}>
            {form === 'rerun'
              ? 'The photographs this attempt kept are judged again by the normal pipeline, exactly like a new submission. A pass registers the ID fingerprint that blocks the same document verifying a second account. Re-judging the same frames often reaches the same answer, so this catches a model blip rather than frames that do not show a live head turn.'
              : 'This grants the badge on your own reading of the photographs above. It cannot register the ID fingerprint, so the same document could later verify a second account. Recorded against your account, permanently.'}
          </p>
          {form === 'approve' && duplicate ? (
            <p role="alert" style={{ fontFamily: 'Inter', fontSize: 12, color: 'var(--c-state-danger, #C7563E)', margin: 0 }}>
              This ID is already registered to another verified account, which is why the check was
              rejected. Approving it would put a badge on two accounts holding the same document.
              Revoke the other account’s badge first, then run this check again.
            </p>
          ) : null}
          {form === 'approve' && minorBand ? (
            <p role="alert" style={{ fontFamily: 'Inter', fontSize: 12, color: 'var(--c-state-danger, #C7563E)', margin: 0 }}>
              This check read the age band “{ageBand}”. Approving by hand would skip the protections
              a minor account gets, so use Run it again instead.
            </p>
          ) : null}
          {form === 'approve' ? (
            <>
              <p style={{ fontFamily: 'Inter', fontSize: 12, color: 'var(--c-fg-secondary)', margin: 0 }}>
                Legal name on the account{onFile ? '' : ' (none on file — read it off the ID)'}
                {nameVerdict ? ` · the check matched it “${nameVerdict}” against the document` : ''}
              </p>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <input
                  value={first}
                  onChange={(e) => setFirst(e.target.value)}
                  placeholder="First name"
                  style={inputStyle}
                />
                <input
                  value={last}
                  onChange={(e) => setLast(e.target.value)}
                  placeholder="Last name"
                  style={inputStyle}
                />
              </div>
              {!last.trim() ? (
                <p style={{ fontFamily: 'Inter', fontSize: 12, color: 'var(--c-state-danger, #C7563E)', margin: 0 }}>
                  No surname. Check the ID before approving — a one-word legal name is usually a
                  gap in the account, not what the document says.
                </p>
              ) : null}
            </>
          ) : null}
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder={
              form === 'rerun'
                ? 'Why is this worth judging again? At least 10 characters.'
                : 'Why are you approving this by hand? At least 10 characters.'
            }
            style={{
              fontFamily: 'Inter',
              fontSize: 13,
              padding: '6px 8px',
              borderRadius: 6,
              border: '1px solid var(--c-border-hairline)',
              background: 'var(--c-bg-surface-alt)',
              color: 'var(--c-fg-default)',
              resize: 'vertical',
            }}
          />
          {err ? (
            <p role="alert" style={{ fontFamily: 'Inter', fontSize: 12, color: 'var(--c-state-danger, #C7563E)', margin: 0 }}>
              {err}
            </p>
          ) : null}
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              type="button"
              disabled={busy || reason.trim().length < 10 || (form === 'approve' && approveBlocked)}
              onClick={() => void submit(form)}
              style={{
                ...(form === 'rerun' ? btn.confirm : btn.danger),
                opacity: busy || reason.trim().length < 10 || (form === 'approve' && approveBlocked) ? 0.5 : 1,
              }}
            >
              {busy ? 'Working…' : form === 'rerun' ? 'Run it again' : 'Approve by hand'}
            </button>
            <button type="button" onClick={() => setForm(null)} style={btn.plain}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  fontFamily: 'Inter',
  fontSize: 13,
  padding: '6px 8px',
  borderRadius: 6,
  border: '1px solid var(--c-border-hairline)',
  background: 'var(--c-bg-surface-alt)',
  color: 'var(--c-fg-default)',
  minWidth: 140,
};

const btn: Record<'plain' | 'confirm' | 'danger', React.CSSProperties> = {
  plain: {
    background: 'transparent',
    border: '1px solid var(--c-border-strong)',
    borderRadius: 6,
    padding: '5px 12px',
    fontFamily: 'Inter',
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--c-fg-default)',
    cursor: 'pointer',
  },
  confirm: {
    background: 'var(--c-fg-default)',
    color: 'var(--c-bg-page)',
    border: 'none',
    borderRadius: 6,
    padding: '6px 14px',
    fontFamily: 'Inter',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  danger: {
    background: 'transparent',
    border: '1px solid #C7563E',
    borderRadius: 6,
    padding: '5px 12px',
    fontFamily: 'Inter',
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--c-state-danger, #C7563E)',
    cursor: 'pointer',
  },
};

function LanePill({ lane, paid }: { lane: string; paid: boolean }) {
  if (lane !== 'priority') return null;
  return (
    <span
      style={{
        background: '#E8DFF0',
        color: '#5A3A7A',
        borderRadius: 9999,
        padding: '2px 10px',
        fontFamily: 'Inter',
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.04em',
      }}
    >
      {paid ? 'PRIORITY · PAID' : 'PRIORITY'}
    </span>
  );
}

function credentialLine(row: {
  credential_code: string | null;
  credential_other_label: string | null;
  credential_pin: string | null;
  credential_label: string | null;
  sub_jurisdiction: string | null;
  credential_specialty: string | null;
}): string | null {
  if (!row.credential_code) return null;
  if (row.credential_code === 'OTHER') {
    return `OTHER: "${row.credential_other_label ?? '?'}" — needs mapping`;
  }
  // pin/label come from the credentials-table join. State-bound pins
  // swap the country code for the state, same rule as the worker.
  const pin =
    row.sub_jurisdiction && row.credential_pin
      ? row.credential_pin.replace(/^[A-Z]{2}\s/, `${row.sub_jurisdiction.toUpperCase()} `)
      : row.credential_pin;
  const spec = row.credential_specialty ? `, ${row.credential_specialty}` : '';
  return `${pin ?? row.credential_code} — ${row.credential_label ?? row.credential_code}${spec}`;
}

function QueueRow({
  row,
  open,
  onOpen,
}: {
  row: VerificationRequestRow;
  open: boolean;
  onOpen: () => void;
}) {
  const st = STATUS_STYLE[row.status] ?? STATUS_STYLE.pending!;
  const cred = credentialLine(row);
  return (
    <li
      style={{
        background: 'var(--c-bg-surface)',
        border: open ? '1px solid var(--c-border-strong)' : '1px solid var(--c-border-hairline)',
        borderRadius: 8,
        padding: '12px 16px',
      }}
    >
      <button
        type="button"
        onClick={onOpen}
        style={{
          display: 'block',
          width: '100%',
          textAlign: 'left',
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'Inter', fontWeight: 600, fontSize: 14, color: 'var(--c-fg-default)' }}>
            {TYPE_LABEL[row.type] ?? row.type}
          </span>
          <LanePill lane={row.lane} paid={row.paid} />
          <span
            style={{
              background: st.bg,
              color: st.fg,
              borderRadius: 9999,
              padding: '2px 10px',
              fontFamily: 'Inter',
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            {row.status.replace(/_/g, ' ')}
          </span>
          <span style={{ marginLeft: 'auto', fontFamily: 'Inter', fontSize: 12, color: 'var(--c-fg-tertiary)' }}>
            {parseServerDate(row.created_at).toLocaleString()}
          </span>
        </div>
        <div style={{ marginTop: 6, fontFamily: 'Inter', fontSize: 13, color: 'var(--c-fg-secondary)' }}>
          @{row.user_handle} · real name: {row.real_name_first} {row.real_name_last} ·{' '}
          {row.document_count} document{row.document_count === 1 ? '' : 's'}
          {cred ? ` · ${cred}` : ''}
        </div>
      </button>
    </li>
  );
}

/**
 * ReviewEvidence — the substance of a standing claim, for the reviewer.
 *
 * Everything the combined submission (§2/§5) collects that the old detail
 * panel was blind to: notability category + evidence links (flagged
 * independent vs self-published, because §5 says self-published sources don't
 * count toward the bar), other accounts, role evidence, the confirmed work
 * email + what domain we recognised it as, any domain-control badge already
 * proved, and the V5 identity gate.
 *
 * The identity gate is shown loudly when it's UNMET, because approving a
 * standing claim while Stage 1 is incomplete just 409s — the reviewer should
 * see why before clicking, not after.
 */
function ReviewEvidence({
  detail,
  siblings,
}: {
  detail: VerificationRequestDetail;
  siblings: SubmissionSibling[];
}) {
  const links = detail.evidence_links ?? [];
  const accounts = detail.other_accounts ?? [];
  const independent = links.filter((l) => isIndependentSource(l.url)).length;

  const label = (s: string) =>
    s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const row = (dt: string, dd: React.ReactNode) => (
    <>
      <dt style={{ color: 'var(--c-fg-tertiary)' }}>{dt}</dt>
      <dd style={{ margin: 0, color: 'var(--c-fg-default)' }}>{dd}</dd>
    </>
  );

  return (
    <div style={{ marginTop: 16 }}>
      {/* V5 gate — loud only when unmet. */}
      {!detail.requester_identity_verified ? (
        <div
          role="alert"
          style={{
            fontFamily: 'Inter',
            fontSize: 12,
            lineHeight: 1.5,
            color: '#8F2F1D',
            background: 'rgba(199,86,62,0.09)',
            border: '1px solid #C7563E',
            borderRadius: 6,
            padding: '8px 10px',
            marginBottom: 12,
          }}
        >
          Requester has no active identity verification. A standing claim can’t
          be approved until Stage&nbsp;1 completes.
        </div>
      ) : null}

      {/* Sibling requests from the same submission. */}
      {siblings.length > 0 ? (
        <div style={{ marginBottom: 12, fontFamily: 'Inter', fontSize: 12, color: 'var(--c-fg-secondary)' }}>
          Part of one submission with:{' '}
          {siblings.map((sib, i) => (
            <span key={sib.id}>
              {i > 0 ? ', ' : ''}
              {label(sib.type)} <span style={{ color: 'var(--c-fg-tertiary)' }}>({sib.status})</span>
            </span>
          ))}
        </div>
      ) : null}

      <h3
        style={{
          fontFamily: 'Inter',
          fontWeight: 600,
          fontSize: 13,
          color: 'var(--c-fg-secondary)',
          margin: '0 0 8px',
        }}
      >
        Claim evidence
      </h3>

      <dl
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          gap: '6px 14px',
          fontFamily: 'Inter',
          fontSize: 13,
          margin: 0,
        }}
      >
        {detail.notability_category
          ? row('Category', label(detail.notability_category))
          : null}

        {detail.official_email
          ? row(
              'Work email',
              <>
                {detail.official_email}
                {detail.official_email_entity ? (
                  <span style={{ color: 'var(--c-fg-tertiary)' }}>
                    {' '}
                    — recognised as {detail.official_email_entity}
                  </span>
                ) : detail.official_email_matched === null ? (
                  <span style={{ color: '#9A6A12' }}> — unrecognised domain (reviewer judgement)</span>
                ) : null}{' '}
                <span style={{ color: '#3F5A2A' }}>✓ confirmed</span>
              </>,
            )
          : null}

        {detail.verified_domain
          ? row(
              'Domain',
              <>
                {detail.verified_domain} <span style={{ color: '#3F5A2A' }}>✓ verified</span>
              </>,
            )
          : null}

        {detail.role_evidence
          ? row(
              'Role evidence',
              <span style={{ whiteSpace: 'pre-wrap' }}>{detail.role_evidence}</span>,
            )
          : null}
      </dl>

      {/* Evidence links — clickable, with the independent-source flag §5 asks
          the reviewer to weigh. */}
      {links.length > 0 ? (
        <div style={{ marginTop: 10 }}>
          <div
            style={{
              fontFamily: 'Inter',
              fontSize: 12,
              color: 'var(--c-fg-tertiary)',
              marginBottom: 4,
            }}
          >
            Links — {independent} of {links.length} look independent
            {detail.notability_category && independent === 0
              ? ' (none independent — notability needs ≥1)'
              : ''}
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {links.map((l, i) => {
              const indep = isIndependentSource(l.url);
              return (
                <li key={i} style={{ fontFamily: 'Inter', fontSize: 12 }}>
                  <a
                    href={l.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    style={{ color: 'var(--c-fg-link)', wordBreak: 'break-all' }}
                  >
                    {l.url}
                  </a>
                  <span
                    style={{
                      marginLeft: 6,
                      fontSize: 11,
                      color: indep ? '#3F5A2A' : '#9A6A12',
                    }}
                  >
                    {indep ? 'independent' : 'self-published'}
                  </span>
                  {l.note ? (
                    <span style={{ color: 'var(--c-fg-tertiary)' }}> — {l.note}</span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {accounts.length > 0 ? (
        <div style={{ marginTop: 10 }}>
          <div
            style={{
              fontFamily: 'Inter',
              fontSize: 12,
              color: 'var(--c-fg-tertiary)',
              marginBottom: 4,
            }}
          >
            Other accounts
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {accounts.map((a, i) => (
              <li key={i} style={{ fontFamily: 'Inter', fontSize: 12 }}>
                <a
                  href={a.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  style={{ color: 'var(--c-fg-link)', wordBreak: 'break-all' }}
                >
                  {a.url}
                </a>
                {a.label ? <span style={{ color: 'var(--c-fg-tertiary)' }}> — {a.label}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function DetailPanel({
  detail,
  siblings,
  note,
  setNote,
  pending,
  busy,
  reversals,
  reverseReason,
  setReverseReason,
  reverseArmed,
  onReverse,
  onDecide,
  onClose,
  onReload,
}: {
  detail: VerificationRequestDetail;
  siblings: SubmissionSibling[];
  note: string;
  setNote: (v: string) => void;
  pending: PendingDecision;
  busy: boolean;
  /** Overturned AI decisions on this request, newest first. */
  reversals: VerificationReversal[];
  reverseReason: string;
  setReverseReason: (v: string) => void;
  reverseArmed: boolean;
  onReverse: () => void;
  onDecide: (action: 'approve' | 'reject' | 'needs_more_info') => void;
  onClose: () => void;
  /** Re-fetch the detail (after assign-credential). */
  onReload: () => void;
}) {
  const cred = credentialLine(detail);
  // Evidence viewer index (null = closed). Images only — PDFs still open in a
  // tab, since the browser renders those better than we can.
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const viewable = detail.documents.filter((d) => d.content_type.startsWith('image/'));
  // Scoped keys come from the IDV host with a staff pass (blob URLs); legacy keys via the bridge.
  const docUrl = useIdvDocUrls(detail.documents.map((d) => d.key), detail.user_id);
  const decidable = ['pending', 'in_review', 'needs_more_info'].includes(detail.status);
  // An OTHER credential must be mapped to a real catalog row before
  // approve (the worker 409s otherwise — an unvetted user-typed string
  // must never become a profile pin).
  const otherUnmapped =
    detail.type === 'credentialed_expert' && detail.credential_code === 'OTHER';
  // Only the automated lanes are reversible here. A human reviewer's decision
  // is reversed by talking to that reviewer — the worker enforces the same
  // rule, so this is the affordance matching the contract, not the contract.
  const aiDecided = Boolean(detail.decided_by?.startsWith('system:'));
  const reversible = !decidable && aiDecided && ['approved', 'rejected'].includes(detail.status);
  return (
    <section
      style={{
        marginTop: 18,
        background: 'var(--c-bg-surface)',
        border: '1px solid var(--c-border-strong)',
        borderRadius: 8,
        padding: 20,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
        <h2 style={{ fontFamily: 'Inter', fontWeight: 700, fontSize: 16, color: 'var(--c-fg-default)', margin: 0 }}>
          {TYPE_LABEL[detail.type] ?? detail.type} — @{detail.user_handle}
        </h2>
        <button
          type="button"
          onClick={onClose}
          style={{
            marginLeft: 'auto',
            background: 'transparent',
            border: 'none',
            color: 'var(--c-fg-secondary)',
            fontFamily: 'Inter',
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          Close
        </button>
      </div>

      <dl
        style={{
          display: 'grid',
          gridTemplateColumns: 'max-content 1fr',
          gap: '6px 16px',
          margin: '14px 0 0',
          fontFamily: 'Inter',
          fontSize: 13,
        }}
      >
        <dt style={{ color: 'var(--c-fg-tertiary)' }}>Real name</dt>
        <dd style={{ margin: 0, color: 'var(--c-fg-default)', fontWeight: 600 }}>
          {detail.real_name_first} {detail.real_name_last}
        </dd>
        <dt style={{ color: 'var(--c-fg-tertiary)' }}>Display name today</dt>
        <dd style={{ margin: 0, color: 'var(--c-fg-default)' }}>
          {detail.user_display_name}
          {detail.user_real_name ? ` (real name on file: ${detail.user_real_name})` : ''}
        </dd>
        {cred ? (
          <>
            <dt style={{ color: 'var(--c-fg-tertiary)' }}>Credential</dt>
            <dd style={{ margin: 0, color: 'var(--c-fg-default)' }}>{cred}</dd>
          </>
        ) : null}
        {detail.registry_id ? (
          <>
            <dt style={{ color: 'var(--c-fg-tertiary)' }}>Registry ID</dt>
            <dd style={{ margin: 0, color: 'var(--c-fg-default)' }}>{detail.registry_id}</dd>
          </>
        ) : null}
        <dt style={{ color: 'var(--c-fg-tertiary)' }}>Account</dt>
        <dd style={{ margin: 0, color: 'var(--c-fg-default)' }}>
          joined {parseServerDate(detail.user_created_at).toLocaleDateString()}
          {detail.user_bio ? ` · "${detail.user_bio.slice(0, 120)}"` : ''}
        </dd>
      </dl>

      <ReviewEvidence detail={detail} siblings={siblings} />

      {/* Evidence — images inline, PDFs open in a tab. Bytes come through
          the Access-gated bridge; nothing here is publicly reachable. */}
      <div style={{ marginTop: 16 }}>
        <h3 style={{ fontFamily: 'Inter', fontWeight: 600, fontSize: 13, color: 'var(--c-fg-secondary)', margin: '0 0 8px' }}>
          Documents ({detail.documents.length})
        </h3>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {detail.documents.map((d) =>
            d.content_type.startsWith('image/') ? (
              <a
                key={d.key}
                href={docUrl(d.key)}
                onClick={(e) => {
                  if (e.metaKey || e.ctrlKey || e.shiftKey) return;
                  e.preventDefault();
                  // Index into the IMAGE-only list the viewer receives, not the
                  // full document list — otherwise a PDF earlier in the set
                  // shifts every index and the wrong page opens.
                  const at = viewable.findIndex((v) => v.key === d.key);
                  if (at >= 0) setViewerIndex(at);
                }}
              >
                <img
                  src={docUrl(d.key)}
                  alt={d.name}
                  style={{
                    width: 180,
                    height: 120,
                    objectFit: 'cover',
                    borderRadius: 6,
                    border: '1px solid var(--c-border-hairline)',
                    display: 'block',
                    cursor: 'zoom-in',
                  }}
                />
              </a>
            ) : (
              <a
                key={d.key}
                href={docUrl(d.key)}
                target="_blank"
                rel="noreferrer"
                style={{
                  fontFamily: 'Inter',
                  fontSize: 13,
                  color: 'var(--c-fg-link)',
                  border: '1px solid var(--c-border-hairline)',
                  borderRadius: 6,
                  padding: '10px 14px',
                }}
              >
                {d.name} ({Math.round(d.size / 1024)} KB)
              </a>
            ),
          )}
        </div>
        {viewerIndex != null && viewable.length > 0 ? (
          <EvidenceLightbox
            items={viewable.map((d) => ({
              url: docUrl(d.key),
              label: d.name,
              isImage: true,
            }))}
            index={viewerIndex}
            onIndex={setViewerIndex}
            onClose={() => setViewerIndex(null)}
          />
        ) : null}
      </div>

      {decidable && otherUnmapped ? (
        <OtherCredentialMapper detail={detail} onMapped={onReload} />
      ) : null}

      {decidable ? (
        <>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Decision note (sent to the user on reject / needs-more-info; kept on the audit row for approve)"
            rows={2}
            style={{
              width: '100%',
              marginTop: 16,
              padding: '8px 10px',
              fontFamily: 'Inter',
              fontSize: 13,
              border: '1px solid var(--c-border-hairline)',
              borderRadius: 6,
              background: 'var(--c-bg-page)',
              color: 'var(--c-fg-default)',
              resize: 'vertical',
            }}
          />
          <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
            <button
              type="button"
              disabled={busy || otherUnmapped}
              title={otherUnmapped ? 'Map the Other credential first' : undefined}
              onClick={() => onDecide('approve')}
              style={{
                background: pending?.action === 'approve' ? '#3F5A2A' : '#5A7A4A',
                color: '#FAF7F2',
                border: 'none',
                borderRadius: 8,
                padding: '8px 18px',
                fontFamily: 'Inter',
                fontWeight: 600,
                fontSize: 13,
                cursor: 'pointer',
                opacity: otherUnmapped ? 0.45 : busy ? 0.6 : 1,
              }}
            >
              {pending?.action === 'approve' ? 'Click again to approve' : 'Approve'}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => onDecide('reject')}
              style={{
                background: pending?.action === 'reject' ? 'var(--c-state-danger, #C7563E)' : 'transparent',
                color: pending?.action === 'reject' ? '#FAF7F2' : 'var(--c-state-danger, #C7563E)',
                border: '1px solid #C7563E',
                borderRadius: 8,
                padding: '8px 18px',
                fontFamily: 'Inter',
                fontWeight: 600,
                fontSize: 13,
                cursor: 'pointer',
                opacity: busy ? 0.6 : 1,
              }}
            >
              {pending?.action === 'reject' ? 'Click again to reject' : 'Reject'}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => onDecide('needs_more_info')}
              style={{
                background: 'transparent',
                color: 'var(--c-fg-secondary)',
                border: '1px solid var(--c-border-hairline)',
                borderRadius: 8,
                padding: '8px 18px',
                fontFamily: 'Inter',
                fontSize: 13,
                cursor: 'pointer',
                opacity: busy ? 0.6 : 1,
              }}
            >
              Needs more info
            </button>
          </div>
        </>
      ) : (
        <p style={{ fontFamily: 'Inter', fontSize: 13, color: 'var(--c-fg-secondary)', marginTop: 16 }}>
          Decided {detail.decided_at ? parseServerDate(detail.decided_at).toLocaleString() : ''} by{' '}
          {detail.decided_by ?? '—'}
          {detail.decision_note ? ` — "${detail.decision_note}"` : ''}
        </p>
      )}

      {reversals.length > 0 ? <ReversalHistory reversals={reversals} /> : null}

      {reversible ? (
        <ReverseAiDecision
          detail={detail}
          reason={reverseReason}
          setReason={setReverseReason}
          armed={reverseArmed}
          busy={busy}
          onReverse={onReverse}
        />
      ) : null}
    </section>
  );
}

/**
 * The record of an overturned AI decision. Shown on the reopened request as
 * well as the decided one, because a reopened row otherwise looks like a fresh
 * submission: reversing clears status, decided_by and decided_at, which is
 * what puts it back in the queue.
 */
function ReversalHistory({ reversals }: { reversals: VerificationReversal[] }) {
  return (
    <div
      style={{
        marginTop: 16,
        padding: '12px 14px',
        border: '1px solid var(--c-border-hairline)',
        borderRadius: 8,
        background: 'var(--c-bg-surface-alt)',
      }}
    >
      <div
        style={{
          fontFamily: 'Inter',
          fontWeight: 600,
          fontSize: 12,
          letterSpacing: 0.4,
          textTransform: 'uppercase',
          color: 'var(--c-fg-secondary)',
        }}
      >
        Reversed automated decision{reversals.length > 1 ? 's' : ''}
      </div>
      {reversals.map((r) => (
        <div key={r.id} style={{ marginTop: 10 }}>
          <p
            style={{
              margin: 0,
              fontFamily: 'Inter',
              fontSize: 13,
              color: 'var(--c-fg-default)',
              lineHeight: 1.5,
            }}
          >
            <strong>{r.reversed_by}</strong> overturned a{' '}
            <strong>{r.prior_status}</strong> decision by{' '}
            <strong>{r.prior_decided_by}</strong> on{' '}
            {parseServerDate(r.created_at).toLocaleString()}.
          </p>
          <p
            style={{
              margin: '4px 0 0',
              fontFamily: 'Inter',
              fontSize: 13,
              color: 'var(--c-fg-secondary)',
              lineHeight: 1.5,
            }}
          >
            “{r.reason}”
          </p>
          {r.revoked_verification ? (
            <p style={{ margin: '4px 0 0', fontFamily: 'Inter', fontSize: 12, color: 'var(--c-state-danger, #C7563E)' }}>
              The badge it had written was revoked
              {r.cascaded_vouches > 0
                ? `, and ${r.cascaded_vouches} vouched badge(s) fell with it`
                : ''}
              .
            </p>
          ) : null}
          {r.evidence_present === false ? (
            <p
              style={{ margin: '4px 0 0', fontFamily: 'Inter', fontSize: 12, color: 'var(--c-fg-secondary)' }}
            >
              The submitted files were already gone from storage at that point.
            </p>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/**
 * Reverse an AI decision (founder 2026-09-21). Deliberately NOT a third button
 * beside Approve / Reject: this does not decide anything. It reopens the
 * request so the ordinary controls apply, and on an AI APPROVAL it also
 * revokes a badge that is already public — which is a different kind of act
 * from grading a pending claim and should not sit in the same row.
 *
 * The two warnings below are the ones a reviewer cannot recover from by
 * undoing:
 *   - reversing an approval revokes a live badge, and every badge that badge
 *     vouched for (the 2026-06-13 cascade);
 *   - a certificate the AI lane decided BEFORE 2026-09-21 was deleted at
 *     decision and cannot be recovered. Newer ones are retained. The server
 *     probes storage and reports what it actually found; this text is the
 *     standing warning before it does.
 */
function ReverseAiDecision({
  detail,
  reason,
  setReason,
  armed,
  busy,
  onReverse,
}: {
  detail: VerificationRequestDetail;
  reason: string;
  setReason: (v: string) => void;
  armed: boolean;
  busy: boolean;
  onReverse: () => void;
}) {
  const wasApproved = detail.status === 'approved';
  const hasBadge = wasApproved && Boolean(detail.verification_id);
  const short = reason.trim().length < 10;
  return (
    <div
      style={{
        marginTop: 16,
        padding: '14px',
        border: '1px solid #C7563E',
        borderRadius: 8,
        background: 'var(--c-bg-surface-alt)',
      }}
    >
      <div
        style={{
          fontFamily: 'Inter',
          fontWeight: 700,
          fontSize: 13,
          color: 'var(--c-fg-default)',
        }}
      >
        Reverse this automated decision
      </div>
      <p
        style={{
          margin: '6px 0 0',
          fontFamily: 'Inter',
          fontSize: 13,
          lineHeight: 1.55,
          color: 'var(--c-fg-secondary)',
        }}
      >
        {detail.decided_by} decided this one. Reversing puts the request back in the queue as
        <strong> In review</strong> so you can approve or reject it yourself. It does not decide
        anything on its own.
      </p>
      {hasBadge ? (
        <p style={{ margin: '8px 0 0', fontFamily: 'Inter', fontSize: 13, lineHeight: 1.55, color: 'var(--c-state-danger, #C7563E)' }}>
          This approval already wrote a live badge. Reversing revokes it, and revokes every badge
          it vouched for.
        </p>
      ) : null}
      {detail.type === 'credentialed_expert' ? (
        <p
          style={{ margin: '8px 0 0', fontFamily: 'Inter', fontSize: 13, lineHeight: 1.55, color: 'var(--c-fg-secondary)' }}
        >
          Certificates decided by the automated lane before 21 Sep 2026 were deleted at decision
          and cannot be recovered; later ones are kept. If the documents above do not open, judge
          this on your own registry check or send it back for more info.
        </p>
      ) : null}
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Why is the automated decision wrong? (required, kept on the audit record)"
        rows={2}
        style={{
          width: '100%',
          marginTop: 10,
          padding: '8px 10px',
          fontFamily: 'Inter',
          fontSize: 13,
          border: '1px solid var(--c-border-hairline)',
          borderRadius: 6,
          background: 'var(--c-bg-page)',
          color: 'var(--c-fg-default)',
          resize: 'vertical',
        }}
      />
      <button
        type="button"
        disabled={busy || short}
        title={short ? 'Give a reason of at least 10 characters' : undefined}
        onClick={onReverse}
        style={{
          marginTop: 10,
          background: armed ? 'var(--c-state-danger, #C7563E)' : 'transparent',
          color: armed ? '#FAF7F2' : 'var(--c-state-danger, #C7563E)',
          border: '1px solid #C7563E',
          borderRadius: 8,
          padding: '8px 18px',
          fontFamily: 'Inter',
          fontWeight: 600,
          fontSize: 13,
          cursor: busy || short ? 'default' : 'pointer',
          opacity: busy || short ? 0.5 : 1,
        }}
      >
        {armed ? 'Click again to reverse' : 'Reverse and reopen'}
      </button>
    </div>
  );
}


/**
 * Other → catalog promotion. Two paths, one box:
 *   - assign an EXISTING credential (search the catalog)
 *   - or CREATE a new credentials row (prefilled from the user's typed
 *     label) and assign it in one shot.
 * Either path ends with the request's credential_code mapped, which
 * unlocks Approve.
 */
function OtherCredentialMapper({
  detail,
  onMapped,
}: {
  detail: VerificationRequestDetail;
  onMapped: () => void;
}) {
  const [q, setQ] = useState(
    detail.profession_other_label ?? detail.credential_other_label ?? '',
  );
  const [results, setResults] = useState<AdminProfession[]>([]);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // V2 requests captured the COUNTRY at submit; legacy OTHER rows didn't, so
  // the reviewer supplies it. Never invent one — the pin binds to it.
  const [country, setCountry] = useState(detail.claim_country ?? '');
  const [subJur, setSubJur] = useState(detail.sub_jurisdiction ?? '');

  const typed = detail.profession_other_label ?? detail.credential_other_label ?? '';
  // V2: the suggested code is the PROFESSION alone — no country prefix. The
  // compound text is derived at assign time; minting prefixed codes here is
  // exactly the compounding V2 removed.
  const suggestedCode = typed
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 30);
  const [form, setForm] = useState({
    code: suggestedCode,
    title: '',
    label: typed,
    category: 'skilled_trades',
    registryIdLabel: 'License number',
    registry: '',
    subNationalDefault: false,
    allowsSpecialty: true,
  });

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => {
      verificationApi
        .professionsAdmin(q)
        .then((r) => {
          if (!cancelled) setResults(r.professions.filter((pr) => pr.status === 'active'));
        })
        .catch(() => {});
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q]);

  const countryOk = /^[A-Z]{2}$/.test(country.toUpperCase());

  async function assign(code: string) {
    if (!countryOk) {
      setErr('Set the credential country (ISO-2, e.g. GH) first.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await verificationApi.assignProfession(detail.id, {
        professionCode: code,
        country: country.toUpperCase(),
        subJurisdiction: subJur.trim() || null,
      });
      onMapped();
    } catch (e) {
      setErr(
        e instanceof VerificationApiError && e.code === 'missing_sub_jurisdiction'
          ? 'This profession is state-licensed — add the state code.'
          : 'Assign failed.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function createAndAssign() {
    if (!countryOk) {
      setErr('Set the credential country (ISO-2, e.g. GH) first.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const { code } = await verificationApi.addProfession({
        ...form,
        // The reviewer-supplied registry binds to the REQUESTER's country as a
        // sparse override; the profession itself stays country-agnostic.
        registryCountry: form.registry ? country.toUpperCase() : undefined,
      });
      await verificationApi.assignProfession(detail.id, {
        professionCode: code,
        country: country.toUpperCase(),
        subJurisdiction: subJur.trim() || null,
      });
      onMapped();
    } catch (e) {
      setErr(
        e instanceof VerificationApiError && e.code === 'code_exists'
          ? 'That profession code already exists — search and assign it instead.'
          : e instanceof VerificationApiError && e.code === 'invalid_profession_fields'
            ? 'Fill code, title, and label.'
            : 'Create failed.',
      );
    } finally {
      setBusy(false);
    }
  }

  const field = (label: string, key: keyof typeof form, width = '1fr') => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 3, width }}>
      <span style={{ fontFamily: 'Inter', fontSize: 11, color: 'var(--c-fg-tertiary)' }}>{label}</span>
      <input
        value={String(form[key])}
        onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        style={{
          fontFamily: 'Inter',
          fontSize: 13,
          padding: '6px 8px',
          border: '1px solid var(--c-border-hairline)',
          borderRadius: 6,
          background: 'var(--c-bg-page)',
          color: 'var(--c-fg-default)',
        }}
      />
    </label>
  );

  return (
    <section
      style={{
        marginTop: 16,
        border: '1px dashed var(--c-border-strong)',
        borderRadius: 8,
        padding: 14,
      }}
    >
      <h3 style={{ fontFamily: 'Inter', fontWeight: 700, fontSize: 13, color: 'var(--c-fg-default)', margin: 0 }}>
        Map "Other" profession: "{typed}"
      </h3>
      <p style={{ fontFamily: 'Inter', fontSize: 12, color: 'var(--c-fg-secondary)', margin: '6px 0 10px' }}>
        Map to an existing profession, or add a NEW one — professions are
        country-agnostic, so a new one serves every country at once. The
        country below binds this request's pin. Approve unlocks once mapped.
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3, width: 140 }}>
          <span style={{ fontFamily: 'Inter', fontSize: 11, color: 'var(--c-fg-tertiary)' }}>
            Country (ISO-2)
          </span>
          <input
            value={country}
            onChange={(e) => setCountry(e.target.value.toUpperCase().slice(0, 2))}
            placeholder="GH"
            style={{
              fontFamily: 'Inter',
              fontSize: 13,
              padding: '7px 10px',
              border: `1px solid ${countryOk ? 'var(--c-border-hairline)' : '#C7563E'}`,
              borderRadius: 6,
              background: 'var(--c-bg-page)',
              color: 'var(--c-fg-default)',
            }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3, width: 120 }}>
          <span style={{ fontFamily: 'Inter', fontSize: 11, color: 'var(--c-fg-tertiary)' }}>
            State (if state-licensed)
          </span>
          <input
            value={subJur}
            onChange={(e) => setSubJur(e.target.value.toUpperCase().slice(0, 3))}
            placeholder="CA"
            style={{
              fontFamily: 'Inter',
              fontSize: 13,
              padding: '7px 10px',
              border: '1px solid var(--c-border-hairline)',
              borderRadius: 6,
              background: 'var(--c-bg-page)',
              color: 'var(--c-fg-default)',
            }}
          />
        </label>
      </div>

      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search professions…"
        style={{
          width: '100%',
          fontFamily: 'Inter',
          fontSize: 13,
          padding: '7px 10px',
          border: '1px solid var(--c-border-hairline)',
          borderRadius: 6,
          background: 'var(--c-bg-page)',
          color: 'var(--c-fg-default)',
        }}
      />
      {results.length > 0 ? (
        <ul style={{ listStyle: 'none', margin: '6px 0 0', padding: 0, maxHeight: 160, overflowY: 'auto' }}>
          {results.map((c) => (
            <li key={c.code} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
              <span style={{ fontFamily: 'Inter', fontSize: 13, color: 'var(--c-fg-default)', flex: 1 }}>
                <strong>{c.label}</strong> ({c.title})
              </span>
              <button
                type="button"
                disabled={busy}
                onClick={() => void assign(c.code)}
                style={{
                  fontFamily: 'Inter',
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--c-fg-link)',
                  background: 'transparent',
                  border: '1px solid var(--c-border-hairline)',
                  borderRadius: 6,
                  padding: '3px 10px',
                  cursor: 'pointer',
                }}
              >
                Assign
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <button
        type="button"
        onClick={() => setCreating((v) => !v)}
        style={{
          marginTop: 10,
          fontFamily: 'Inter',
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--c-fg-link)',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
        }}
      >
        {creating ? 'Hide new-profession form' : '+ Add as a new profession'}
      </button>

      {creating ? (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {field('Code (no country prefix)', 'code')}
            {field('Title (pin suffix, e.g. Crane Op)', 'title', '200px')}
          </div>
          {field('Label (picker text)', 'label')}
          <div style={{ display: 'flex', gap: 8 }}>
            {field('Category', 'category', '160px')}
            {field('Registry-ID label', 'registryIdLabel')}
          </div>
          {field(`Registry for ${country || 'this country'} (optional)`, 'registry')}
          <div style={{ display: 'flex', gap: 16 }}>
            <label style={{ fontFamily: 'Inter', fontSize: 12, color: 'var(--c-fg-secondary)', display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={form.subNationalDefault}
                onChange={(e) => setForm((f) => ({ ...f, subNationalDefault: e.target.checked }))}
              />
              State/province-licensed
            </label>
            <label style={{ fontFamily: 'Inter', fontSize: 12, color: 'var(--c-fg-secondary)', display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={form.allowsSpecialty}
                onChange={(e) => setForm((f) => ({ ...f, allowsSpecialty: e.target.checked }))}
              />
              Allows specialty
            </label>
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={() => void createAndAssign()}
            style={{
              alignSelf: 'flex-start',
              fontFamily: 'Inter',
              fontSize: 13,
              fontWeight: 600,
              background: '#5A7A4A',
              color: '#FAF7F2',
              border: 'none',
              borderRadius: 8,
              padding: '7px 16px',
              cursor: 'pointer',
              opacity: busy ? 0.6 : 1,
            }}
          >
            Create profession + assign to this request
          </button>
        </div>
      ) : null}

      {err ? (
        <p style={{ fontFamily: 'Inter', fontSize: 12, color: 'var(--c-state-danger, #C7563E)', margin: '8px 0 0' }}>{err}</p>
      ) : null}
    </section>
  );
}

// ════════════════════════ Verifications ledger ══════════════════════════
// The `verifications` table itself — the FACTS, not the request queue. This is
// where automated Lane A identity verifications land (no request row exists for
// them) and where the retained government-ID image is surfaced for authorized
// staff (never public; streamed through the same Access-gated bridge).

const VERIF_STATUS_STYLE: Record<string, { bg: string; fg: string }> = {
  active: { bg: '#DFE8D5', fg: '#3F5A2A' },
  provisional: { bg: '#EFE3CF', fg: '#7A5A1A' },
  pending_review: { bg: 'var(--c-bg-surface-alt)', fg: 'var(--c-fg-secondary)' },
  revoked: { bg: '#F4D9D2', fg: '#9A2A12' },
};

const EVIDENCE_LABEL: Record<string, string> = {
  gov_id_ai: 'Automated ID (Lane A)',
  gov_id_thirdparty: 'Third-party ID',
  document_review: 'Manual document review',
  credential_ai: 'Automated credential',
  peer_attestation: 'Peer attestation',
  domain_dns: 'Domain control',
  business_vouch: 'Business vouch',
};

function VerificationsLedger() {
  const [rows, setRows] = useState<VerificationLedgerRow[] | null>(null);
  const [scope, setScope] = useState<'identity' | 'all'>('identity');
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    const t = setTimeout(() => {
      verificationApi
        .verifications(scope, q.trim())
        .then((res) => {
          if (!cancelled) setRows(res.verifications);
        })
        .catch((err) => {
          if (cancelled) return;
          setRows([]);
          setError(
            err instanceof VerificationApiError && err.status === 403
              ? 'Your account does not have verification-review access.'
              : "Couldn't load verifications. Try again.",
          );
        });
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [scope, q]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => setScope((s) => (s === 'identity' ? 'all' : 'identity'))}
          style={{
            background: 'transparent',
            border: '1px solid var(--c-border-hairline)',
            borderRadius: 9999,
            padding: '3px 12px',
            fontFamily: 'Inter',
            fontSize: 12,
            color: 'var(--c-fg-secondary)',
            cursor: 'pointer',
          }}
        >
          {scope === 'identity' ? 'Identity only · show all types' : 'All types · show identity only'}
        </button>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by handle…"
          style={{
            flex: 1,
            minWidth: 160,
            fontFamily: 'Inter',
            fontSize: 13,
            padding: '6px 10px',
            border: '1px solid var(--c-border-hairline)',
            borderRadius: 6,
            background: 'var(--c-bg-page)',
            color: 'var(--c-fg-default)',
          }}
        />
      </div>
      <p style={{ fontFamily: 'Inter', fontSize: 13, color: 'var(--c-fg-secondary)', margin: '0 0 16px' }}>
        The verification facts ledger, newest first. Identity rows include the
        retained government-ID image (kept privately for the life of the account
        + 1 year; reviewer-only, never public).
      </p>

      {error ? (
        <p style={{ fontFamily: 'Inter', fontSize: 14, color: 'var(--c-state-danger, #C7563E)', margin: '24px 0' }}>{error}</p>
      ) : rows === null ? (
        <p style={{ fontFamily: 'Inter', fontSize: 14, color: 'var(--c-fg-secondary)' }}>Loading…</p>
      ) : rows.length === 0 ? (
        <p style={{ fontFamily: 'Inter', fontSize: 14, color: 'var(--c-fg-secondary)', margin: '24px 0' }}>
          No verifications{q ? ` matching “${q}”` : ''}.
        </p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rows.map((v) => (
            <VerificationLedgerCard key={v.id} row={v} />
          ))}
        </ul>
      )}
    </div>
  );
}

/** Read a string-ish metadata field without trusting its type. */
function metaStr(meta: Record<string, unknown> | null, key: string): string | null {
  const v = meta?.[key];
  if (v === null || v === undefined || v === '') return null;
  return typeof v === 'object' ? JSON.stringify(v) : String(v);
}

/**
 * Render a face-match score a reviewer can read without knowing ArcFace.
 *
 * The raw cosine is NOT a percentage and never approaches 1.0 for two distinct
 * photos — even same-person same-session pairs land ~0.6–0.8, and a printed ID
 * portrait (halftone, laminate, often years old) against a live selfie lands
 * lower still. Measured on genuine pairs through this exact pipeline
 * (2026-08-06): four true matches scored 0.47–0.54; one impostor pair scored
 * 0.23; the decision threshold is 0.40. A bare "0.4695" in this ledger read as
 * a system fault to the founder — the number was fine, the presentation wasn't.
 *
 * Bands are deliberately coarse (strong / moderate / below threshold) and the
 * raw value stays visible for anyone comparing across rows. `min_all` method is
 * flagged because that fallback scores the WORST face on the document — on a
 * multi-portrait ID the headline number may not be the main portrait.
 */
function faceMatchDisplay(scoreStr: string, meta: Record<string, unknown> | null): string {
  const s = Number(scoreStr);
  if (!Number.isFinite(s)) return scoreStr;
  const band = s >= 0.47 ? 'strong' : s >= 0.4 ? 'moderate' : 'below threshold';
  const method = meta && typeof meta.face_method === 'string' ? meta.face_method : null;
  const faces = meta && Number.isFinite(Number(meta.face_target_faces)) ? Number(meta.face_target_faces) : null;
  let out = `${s.toFixed(3)} — ${band} (genuine pairs measure ~0.47–0.54; threshold 0.40)`;
  if (method === 'min_all' && faces != null && faces > 1) {
    out += ` · min over ${faces} faces — may reflect the document's worst portrait`;
  }
  return out;
}

/** "drivers_license" → "Driver's license". Registry values are snake_case. */
const DOC_TYPE_LABEL: Record<string, string> = {
  drivers_license: "Driver's license",
  passport: 'Passport',
  passport_card: 'Passport card',
  national_id: 'National ID',
  residence_permit: 'Residence permit',
  id_card: 'ID card',
};

/**
 * Caption for a retained capture thumbnail.
 *
 * Prefers the stored `role`; falls back to position for rows retained before
 * the box worker started recording it (front first, back second — the order
 * retainedIdKeys() built them). A lone document is captioned without a side,
 * because "front" implies a missing back that was never captured: only TD1
 * cards (national ID / residence permit) upload a back at all.
 *
 * Roles beyond the ID arrived with the 2026-08-06 retention change (selfie and
 * liveness frames are kept now). Before it, every retained key WAS the ID, so
 * an unhandled role fell through to the document label and captioned a selfie
 * "Passport" — which is exactly what shipped until this was fixed. Handle the
 * roles explicitly and label an unknown one honestly rather than guessing.
 */
function retainedDocLabel(
  doc: { key: string; role?: string },
  index: number,
  docType: string | null,
  poseIndex?: number,
): string {
  const kind = (docType && DOC_TYPE_LABEL[docType]) || 'Government ID';
  const role = doc.role ?? (index === 0 ? 'id_front' : 'id_back');
  switch (role) {
    case 'id_front':
      return kind;
    case 'id_back':
      return `${kind} — back`;
    case 'selfie':
      return 'Selfie';
    case 'pose':
      return poseIndex != null ? `Liveness frame ${poseIndex}` : 'Liveness frame';
    default:
      return 'Capture';
  }
}

function VerificationLedgerCard({ row }: { row: VerificationLedgerRow }) {
  const st = VERIF_STATUS_STYLE[row.status] ?? VERIF_STATUS_STYLE.pending_review!;
  const revoked = row.status === 'revoked' || !!row.revoked_at;
  const meta = row.metadata;
  const docs = row.retained_documents ?? [];
  const docUrl = useIdvDocUrls(docs.map((d) => d.key), row.user_id);
  // null = closed. Index into `docs`, so the viewer opens on the thumbnail that
  // was clicked and can step through the rest.
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);

  // Compact metadata facts for an identity row (safely coerced).
  const facts: Array<[string, string]> = [];
  const country = row.retained_id_country ?? metaStr(meta, 'id_country');
  const docType = row.retained_doc_type ?? metaStr(meta, 'doc_type');
  const nameMatch = metaStr(meta, 'name_match');
  const faceScore = metaStr(meta, 'face_match_score');
  const faceSkipped = meta?.face_skipped === true;
  const liveness = meta?.liveness_passed === true ? 'passed' : meta?.liveness_passed === false ? 'not passed' : null;
  const dobYear = metaStr(meta, 'dob_year');
  const model = metaStr(meta, 'model');
  const structured = metaStr(meta, 'structured_source');
  if (country) facts.push(['Country', country]);
  if (docType) facts.push(['Document', docType.replace(/_/g, ' ')]);
  if (dobYear) facts.push(['Birth year', dobYear]);
  if (nameMatch) facts.push(['Name match', nameMatch]);
  if (faceScore) facts.push(['Face match', faceMatchDisplay(faceScore, meta)]);
  else if (faceSkipped) facts.push(['Face', 'skipped']);
  if (liveness) facts.push(['Liveness', liveness]);
  if (structured) facts.push(['Source', structured]);
  if (model) facts.push(['Model', model]);

  return (
    <li
      style={{
        background: 'var(--c-bg-surface)',
        border: '1px solid var(--c-border-hairline)',
        borderRadius: 8,
        padding: '12px 16px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'Inter', fontWeight: 600, fontSize: 14, color: 'var(--c-fg-default)' }}>
          {TYPE_LABEL[row.type] ?? row.type}
        </span>
        <span
          style={{
            background: st.bg,
            color: st.fg,
            borderRadius: 9999,
            padding: '2px 10px',
            fontFamily: 'Inter',
            fontSize: 11,
            fontWeight: 600,
          }}
        >
          {row.status.replace(/_/g, ' ')}
        </span>
        <span style={{ fontFamily: 'Inter', fontSize: 11, color: 'var(--c-fg-tertiary)' }}>
          {EVIDENCE_LABEL[row.evidence] ?? row.evidence}
        </span>
        <span style={{ marginLeft: 'auto', fontFamily: 'Inter', fontSize: 12, color: 'var(--c-fg-tertiary)' }}>
          {row.verified_at ? parseServerDate(row.verified_at).toLocaleString() : '—'}
        </span>
      </div>

      <div style={{ marginTop: 6, fontFamily: 'Inter', fontSize: 13, color: 'var(--c-fg-secondary)' }}>
        @{row.user_handle}
        {row.user_display_name ? ` · ${row.user_display_name}` : ''}
        {row.user_real_name ? ` · real name: ${row.user_real_name}` : ''}
      </div>

      {facts.length > 0 ? (
        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: '4px 14px' }}>
          {facts.map(([k, val]) => (
            <span key={k} style={{ fontFamily: 'Inter', fontSize: 12, color: 'var(--c-fg-tertiary)' }}>
              {k}: <span style={{ color: 'var(--c-fg-secondary)' }}>{val}</span>
            </span>
          ))}
        </div>
      ) : null}

      {revoked ? (
        <div style={{ marginTop: 8, fontFamily: 'Inter', fontSize: 12, color: 'var(--c-state-danger, #C7563E)' }}>
          Revoked {row.revoked_at ? parseServerDate(row.revoked_at).toLocaleString() : ''}
          {row.revoke_reason ? ` — ${row.revoke_reason.replace(/_/g, ' ')}` : ''}
        </div>
      ) : null}

      {/* Retained government ID — reviewer-only window into the private prefix.
          Streams through the Access-gated /documents/ bridge; images render
          inline, anything else opens in a tab. */}
      {docs.length > 0 ? (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontFamily: 'Inter', fontSize: 12, fontWeight: 600, color: 'var(--c-fg-secondary)', marginBottom: 6 }}>
            Retained captures
            {row.retained_legal_hold ? (
              <span style={{ marginLeft: 8, color: '#9A6A12' }}>· legal hold</span>
            ) : null}
            <span style={{ marginLeft: 8, fontWeight: 400, color: 'var(--c-fg-tertiary)' }}>
              {row.retained_account_deleted_at
                ? `purges ${row.retained_purge_after ? parseServerDate(row.retained_purge_after).toLocaleDateString() : '—'} (account deleted)`
                : 'kept for the life of the account + 1 year'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {docs.map((d, i) => {
              // Number the liveness frames among themselves ("Liveness frame 2"
              // of however many), not by their index in the whole list.
              const poseIndex =
                d.role === 'pose'
                  ? docs.slice(0, i + 1).filter((x) => x.role === 'pose').length
                  : undefined;
              const label = retainedDocLabel(d, i, docType, poseIndex);
              return (
                <figure key={d.key} style={{ margin: 0 }}>
                  {/* Opens the in-page viewer, not a tab: reviewing an identity
                      means comparing the ID against the selfie and the liveness
                      frames, which a tab-per-image makes impossible. The href
                      stays a real URL so middle-click / "open in new tab" and
                      keyboard activation all still behave normally. */}
                  <a
                    href={docUrl(d.key)}
                    onClick={(e) => {
                      if (e.metaKey || e.ctrlKey || e.shiftKey) return;
                      e.preventDefault();
                      setViewerIndex(i);
                    }}
                  >
                    <img
                      src={docUrl(d.key)}
                      alt={label}
                      style={{
                        width: 180,
                        height: 120,
                        objectFit: 'cover',
                        borderRadius: 6,
                        border: '1px solid var(--c-border-hairline)',
                        display: 'block',
                        cursor: 'zoom-in',
                      }}
                    />
                  </a>
                  <figcaption
                    style={{
                      fontFamily: 'Inter',
                      fontSize: 11,
                      color: 'var(--c-fg-tertiary)',
                      marginTop: 4,
                      maxWidth: 180,
                    }}
                  >
                    {label}
                  </figcaption>
                </figure>
              );
            })}
          </div>
          {/* Says out loud what is and is NOT here, so a reviewer never has to
              guess whether something failed to upload or was deliberately
              destroyed — silence reads like a bug either way.

              Verifications decided BEFORE 2026-08-06 kept only the ID and
              destroyed the biometrics at decision time, so their cards show a
              lone document; that is the old policy, not a missing file. */}
          <div
            style={{
              fontFamily: 'Inter',
              fontSize: 11,
              color: 'var(--c-fg-tertiary)',
              marginTop: 8,
            }}
          >
            {docs.some((d) => d.role === 'selfie' || d.role === 'pose')
              ? 'All captures are retained together on one schedule. No face template is stored — the comparison is transient — and nothing scans these images at rest.'
              : 'Only the government ID was retained for this verification; the selfie and liveness frames were destroyed at decision time under the policy in force before 2026-08-06.'}
          </div>
          {viewerIndex != null ? (
            <EvidenceLightbox
              items={docs.map((d, i) => ({
                url: docUrl(d.key),
                label: retainedDocLabel(
                  d,
                  i,
                  docType,
                  d.role === 'pose'
                    ? docs.slice(0, i + 1).filter((x) => x.role === 'pose').length
                    : undefined,
                ),
                isImage: true,
              }))}
              index={viewerIndex}
              onIndex={setViewerIndex}
              onClose={() => setViewerIndex(null)}
            />
          ) : null}
        </div>
      ) : row.type === 'identity_verified' ? (
        <div style={{ marginTop: 10, fontFamily: 'Inter', fontSize: 12, color: 'var(--c-fg-tertiary)' }}>
          No retained ID held (verified before retention, already purged, or the
          image was not kept).
        </div>
      ) : null}
    </li>
  );
}
